import React from 'react';
import { calcDaysStalled } from '../../services/last10Storage';

/**
 * Last10Badge
 *
 * Displays a compact indicator when a goal is in the 80–99% completion range.
 * Optionally shows a "stalled" warning when last activity was too long ago.
 *
 * Props:
 *   completionPercentage  number      current % (80–99)
 *   goal                  Object      goal document (used to check stall metadata)
 *   className             string?     additional Tailwind classes
 */
const Last10Badge = ({ completionPercentage, goal = {}, className = '' }) => {
    const daysStalled = calcDaysStalled(goal);
    const isStalled = daysStalled >= 3 && goal.last10EnteredAt;

    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold
                ${isStalled
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700'
                    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border border-amber-300 dark:border-amber-600'
                } tracking-wide ${className}`}
            title={isStalled ? `Stalled for ${daysStalled} days` : 'Final phase — you are close'}
        >
            {/* Pulse dot */}
            <span className="relative flex h-1.5 w-1.5">
                {!isStalled && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-60" />
                )}
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isStalled ? 'bg-red-500' : 'bg-amber-500'}`} />
            </span>

            {isStalled
                ? `Stalled · ${daysStalled}d`
                : `${completionPercentage}% · Finish Line`
            }
        </span>
    );
};

export default Last10Badge;
